import { useState } from "react";

import { MatomoConsentPreferencesDialog } from "@/components/common/MatomoConsentPreferencesDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useMatomoConsentUi } from "@/contexts/MatomoConsentUiContext";
import { getCachedAuthConfig } from "@/lib/api/auth-config";
import { grantMatomoConsent, revokeMatomoConsent } from "@/lib/matomo/tracker";
import { cn } from "@/lib/utils";

/**
 * Bannière de consentement cookies (grill Q10 B + panneau Personnaliser).
 * Independent from {@link CguAcceptanceGate}.
 * Re-openable from the app footer via {@link useMatomoConsentUi}.
 */
export function MatomoConsentBanner() {
  const { bannerOpen, consent, closeBanner, setConsentState } =
    useMatomoConsentUi();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [draftAudienceEnabled, setDraftAudienceEnabled] = useState(false);

  if (!bannerOpen) {
    return null;
  }

  const isActive = consent === "granted";
  const statusLabel =
    consent === "granted" ? "Accepté" : consent === "denied" ? "Refusé" : null;

  const openPreferences = () => {
    setDraftAudienceEnabled(consent === "granted");
    setPreferencesOpen(true);
  };

  /** Closes the preferences panel first (banner CTAs are inert while the modal Dialog is open). */
  const applyGranted = () => {
    setPreferencesOpen(false);
    grantMatomoConsent({ trackCurrentPage: true });
    setConsentState("granted");
  };

  /** Closes the preferences panel first (banner CTAs are inert while the modal Dialog is open). */
  const applyDenied = () => {
    setPreferencesOpen(false);
    revokeMatomoConsent();
    setConsentState("denied");
  };

  const savePreferences = () => {
    if (draftAudienceEnabled) {
      applyGranted();
      return;
    }
    applyDenied();
  };

  return (
    <>
      <div
        role="dialog"
        aria-label="Gestion des cookies"
        className="fixed inset-x-0 bottom-0 z-50 border-t border-border/50 bg-background/95 shadow-[0_-8px_30px_rgba(0,0,0,0.06)] backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-2.5 pt-6 pb-7 sm:flex-row sm:items-center sm:justify-between sm:gap-6 min-h-40">
          <div className="min-w-0 flex flex-wrap gap-2.5 text-left">
            <div className="flex items-center gap-1.5">
              {statusLabel ? (
                <>
                  <span className="text-muted-foreground font-medium text-sm">
                    Consentement&nbsp;:{" "}
                  </span>
                  <Badge
                    variant="outline"
                    className={cn(
                      "h-5 gap-1.5 border-transparent px-2.5 text-[11px] font-medium tracking-wide",
                      isActive
                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                        : "bg-secondary/10 text-secondary"
                    )}>
                    <span
                      aria-hidden
                      className={cn(
                        "size-1.5 rounded-full",
                        isActive ? "bg-emerald-500" : "bg-secondary"
                      )}
                    />
                    {statusLabel}
                  </Badge>
                </>
              ) : null}
            </div>
            <p className="text-sm leading-snug text-muted-foreground border-l border-border/50 pl-3 ml-1.5">
              Ce site utilise des cookies et vous donne le contrôle sur ceux que
              vous souhaitez activer.
              <br />
              <a
                className="text-primary/80 underline underline-offset-2 hover:text-primary"
                href={`https://www.${getCachedAuthConfig().platformBaseDomain}/general/politique-de-protection-des-donnees`}
                target="_blank">
                Consulter notre politique de confidentialité
              </a>
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
            {consent !== "unknown" ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-24 text-muted-foreground"
                onClick={() => {
                  setPreferencesOpen(false);
                  closeBanner();
                }}>
                Fermer
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openPreferences}>
              Personnaliser
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-24"
              disabled={consent === "denied"}
              aria-pressed={consent === "denied"}
              onClick={applyDenied}>
              Refuser
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              className="w-24"
              disabled={consent === "granted"}
              aria-pressed={consent === "granted"}
              onClick={applyGranted}>
              Accepter
            </Button>
          </div>
        </div>
      </div>

      <MatomoConsentPreferencesDialog
        open={preferencesOpen}
        onOpenChange={setPreferencesOpen}
        draftAudienceEnabled={draftAudienceEnabled}
        onDraftAudienceChange={setDraftAudienceEnabled}
        onSave={savePreferences}
      />
    </>
  );
}
